import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { HeaderContext } from "@tanstack/react-table";

import { useQueryBuilderContext } from "@/contexts/query-builder-context";
import type { Option } from "@/utils";

type SearchHeaderSelectProps<TData, TValue> = {
  header: HeaderContext<TData, TValue>;
  name?: string;
  options: Option[];
};

const ALL_TEXT_VALUE = "all";

export const SearchHeaderSelect = <TData, TValue>({
  header,
  name,
  options,
}: SearchHeaderSelectProps<TData, TValue>) => {
  const { builder: filtersQueryBuilder } = useQueryBuilderContext();
  const search = useSearch({ strict: false }) as Record<string, string | number | undefined>;
  const navigate = useNavigate();

  const headerId = header.column.id;

  const filterValue = search?.[headerId] || "";

  const updateFilter = (text: string) => {
    const newSearch = { ...search };

    newSearch.page = 1;

    if (text === ALL_TEXT_VALUE) {
      delete newSearch[headerId];
      filtersQueryBuilder.removeFilter(headerId);
    } else {
      newSearch[headerId] = text;
      filtersQueryBuilder.filter(headerId, text, true);
    }

    navigate({ to: ".", search: newSearch });
  };

  const optionsOrdered = useMemo(() => {
    return options.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
  }, [options]);

  const selectId = `${headerId}-filter`;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-xs font-semibold tracking-wider whitespace-nowrap uppercase"
        htmlFor={selectId}
      >
        {name}
      </label>

      <select
        className="block h-9 w-full min-w-20 cursor-pointer rounded-lg border border-line-control bg-surface px-2 text-sm font-normal text-content normal-case shadow-raised transition-[color,box-shadow,border-color] duration-150 hover:border-content-subtle focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-focus-ring/40 focus-visible:outline-none"
        id={selectId}
        onChange={(e) => {
          updateFilter(e.target.value);
        }}
        value={filterValue || ALL_TEXT_VALUE}
      >
        <option value={ALL_TEXT_VALUE}>All</option>
        {optionsOrdered.map((option) => {
          let value: string;

          if (typeof option.value === "boolean") {
            value = option.value ? "1" : "0";
          } else {
            value = `${option.value}`;
          }

          return (
            <option key={option.label + "-" + value} value={value}>
              {option.label}
            </option>
          );
        })}
      </select>
    </div>
  );
};
